import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { resizeImage } from "simple-image-resize";
import { UserProfileState } from "../index";
import { useAppSelector } from "../../app";
import { useTranslation } from "react-i18next";
import { notification } from "antd";
import { AxiosError } from "axios";
import { useDispatch } from "react-redux";
import { updateUserProfile } from "../store/userSlice";
import { defaultSettings, getAvatarUrl } from "../utils/utils";
import useAuthAuthenticate from "./useAuthAuthenticate";
import { useUpdateUserMutation } from "../store/userApi";
import Sentry from "../../sentry";
import { clearImageCache } from "../../common/avatar";
import http from "../../common/utilities/HttpModule";

const usePersonalPage = (updateOnBlur = true) => {
  const dispatch = useDispatch();
  const updating = useRef(false);

  const { user, userSettings } = useAppSelector((state) => state.userReducer);

  const disableManagement = !user?.id.toLowerCase().includes("auth0");

  const { fetchTokenAndMetaData } = useAuthAuthenticate(false, true);

  const {
    t,
    i18n: { language },
  } = useTranslation("personalPage", {
    keyPrefix: "userSettings",
  });

  const [state, setState] = useState<UserProfileState>({
    firstName: user?.firstName === "temp" ? "" : user?.firstName || "",
    lastName: user?.lastName === "temp" ? "" : user?.lastName || "",
    email: user?.email || "",
    company: user?.organization.name || "",
    avatar: user?.avatar || "",
  });

  const [hasBeenUpdated, setHasBeenUpdated] = useState(false);

  const [updateUser] = useUpdateUserMutation();

  const handleBlur = useCallback((key: string, value: string) => {
    setState((oldState) => ({
      ...oldState,
      [key]: value,
    }));
    setHasBeenUpdated(true);
  }, []);

  const removeAvatar = useCallback(async () => {
    if (!user) return;

    try {
      await http.delete(`/api/v1/user/${user.id}/avatar`);
      // Clear the cached avatar blob URL
      clearImageCache(getAvatarUrl(user.id));
      setState((prevState) => ({
        ...prevState,
        avatar: user.defaultAvatar || "",
      }));
    } catch (error) {
      console.error("Failed to remove avatar:", error);
    }
  }, [user]);

  const handleAvatarChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files![0];
      if (!file || !user) return;

      const blob = await resizeImage(file, {
        maxWidth: 500,
        maxHeight: 500,
        quality: 0.5,
      });

      // Upload directly to backend via multipart/form-data
      const formData = new FormData();
      formData.append("file", blob, file.name);

      try {
        await http.put(`/api/v1/user/${user.id}/avatar`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        // Clear the cached avatar blob URL and update with cache-busting URL
        clearImageCache(getAvatarUrl(user.id));
        setState((prevState) => ({
          ...prevState,
          avatar: getAvatarUrl(user.id, true),
        }));
      } catch (error) {
        console.error("Failed to upload avatar:", error);
      }
    },
    [user],
  );

  const updateProfile = useCallback(
    async (onboarding = false) => {
      if (updating.current) {
        return;
      }

      if (!user) {
        return;
      }
      try {
        updating.current = true;

        const body = {
          ...state,
          email: state.email.toLowerCase(),
          metadata: {
            ...userSettings,
            overviewSorter: userSettings.saveSorter
              ? userSettings.overviewSorter
              : defaultSettings.overviewSorter,
            archiveSorter: userSettings.saveSorter
              ? userSettings.archiveSorter
              : defaultSettings.archiveSorter,
            overviewFilters: userSettings.saveFilters
              ? userSettings.overviewFilters
              : [],
            archiveFilters: userSettings.saveFilters
              ? userSettings.archiveFilters
              : [],
            showWelcome: false,
            language,
          },
        };

        await updateUser({ userId: user.id, data: body }).unwrap();
        dispatch(
          updateUserProfile({
            ...user,
            ...state,
            email: state.email.toLowerCase(),
            showWelcome: false,
          }),
        );
        notification.success({
          key: "userSettingsUpdatedToast",
          title: t("success.title"),
          description: t("success.description"),
        });
        setHasBeenUpdated(false);
      } catch (e) {
        const error = e as AxiosError;
        const data = error?.response?.data as { message?: string } | undefined;

        console.error(e);
        Sentry.captureException(e, {
          tags: {
            flow: "update_profile",
            onboarding: onboarding.toString(),
          },
          extra: {
            userId: user?.id,
            state,
          },
        });

        notification.error({
          title: t("error.title"),
          description: data?.message || t("error.description"),
        });
      }

      updating.current = false;
    },
    [dispatch, state, userSettings, user, t, language, updateUser],
  );

  useEffect(() => {
    if (!updateOnBlur) {
      return;
    }

    if (!hasBeenUpdated) {
      return;
    }

    // `updateProfile` is an async save-on-blur; its setStates land after the
    // awaited request. This is a side effect (persisting the profile), not
    // derived state, so it belongs in an effect.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- async save-on-blur side effect; see above
    updateProfile(false);
  }, [hasBeenUpdated, updateProfile, updateOnBlur]);

  const updateProfileAndFetchUserInputIds = useCallback(async () => {
    try {
      await updateProfile(true);
      if (user) {
        await fetchTokenAndMetaData();
        return;
      }
      await fetchTokenAndMetaData();
    } catch (e) {
      console.error("Error in updateProfileAndFetchUserInputIds:", e);
      Sentry.captureException(e, {
        tags: {
          flow: "update_profile_and_fetch_user_input_ids",
          onboarding: "true",
        },
        extra: {
          userId: user?.id,
        },
      });
    }
  }, [updateProfile, fetchTokenAndMetaData, user]);

  return {
    state,
    user,
    disableManagement,
    handleBlur,
    removeAvatar,
    handleAvatarChange,
    setHasBeenUpdated,
    updateProfile,
    updateProfileAndFetchUserInputIds,
  };
};

export default usePersonalPage;
